/**
 * Google OAuth utility functions
 * Uses Google Identity Services for authentication
 */

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            callback: (response: { credential: string }) => void;
            use_fedcm_for_prompt?: boolean;
          }) => void;
          prompt: (
            notification?: (notification: {
              isNotDisplayed: boolean;
              isSkippedMoment: boolean;
              isDismissedMoment: boolean;
            }) => void
          ) => void;
          renderButton: (
            element: HTMLElement,
            config: {
              type?: string;
              theme?: string;
              size?: string;
              text?: string;
              shape?: string;
              logo_alignment?: string;
              width?: string;
              locale?: string;
            }
          ) => void;
        };
        oauth2: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (response: { access_token: string }) => void;
            error_callback?: (error: { type: string; message: string }) => void;
          }) => {
            requestAccessToken: () => void;
          };
        };
      };
    };
  }
}

/**
 * Load Google Identity Services script
 */
export const loadGoogleScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.google?.accounts?.id) {
      resolve();
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector('script[src*="accounts.google.com/gsi/client"]');
    if (existingScript) {
      existingScript.addEventListener("load", () => resolve());
      existingScript.addEventListener("error", () =>
        reject(new Error("Failed to load Google script"))
      );
      return;
    }

    // Load Google Identity Services script
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Google script"));
    document.head.appendChild(script);
  });
};

/**
 * Initialize Google Sign-In
 * @param clientId - Google OAuth client ID
 * @param callback - Callback function that receives the idToken
 * @param useFedCM - Whether to use FedCM (default: false to avoid cancellation issues)
 */
export const initializeGoogleSignIn = (
  clientId: string,
  callback: (idToken: string) => void,
  useFedCM: boolean = false
): void => {
  if (!window.google?.accounts?.id) {
    throw new Error("Google Identity Services not loaded. Call loadGoogleScript() first.");
  }

  window.google.accounts.id.initialize({
    client_id: clientId,
    callback: (response) => {
      callback(response.credential);
    },
    use_fedcm_for_prompt: useFedCM,
  });
};

/**
 * Trigger Google Sign-In using button click (avoids FedCM issues)
 * Creates a hidden Google button and programmatically clicks it
 * @param clientId - Google OAuth client ID
 * @param callback - Callback function that receives the idToken
 * @param onError - Optional error callback
 */
export const triggerGoogleSignIn = (
  clientId: string,
  callback: (idToken: string) => void,
  onError?: (error: Error) => void
): void => {
  if (!window.google?.accounts?.id) {
    const error = new Error("Google Identity Services not loaded. Call loadGoogleScript() first.");
    onError?.(error);
    throw error;
  }

  try {
    // Create a temporary hidden container for the Google button
    const tempContainer = document.createElement("div");
    tempContainer.style.position = "fixed";
    tempContainer.style.left = "-9999px";
    tempContainer.style.opacity = "0";
    tempContainer.style.pointerEvents = "none";
    document.body.appendChild(tempContainer);

    // Initialize Google Sign-In
    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: (response) => {
        // Clean up temporary container
        document.body.removeChild(tempContainer);
        callback(response.credential);
      },
    });

    // Render Google button in hidden container
    window.google.accounts.id.renderButton(tempContainer, {
      type: "standard",
      theme: "outline",
      size: "large",
      text: "signin_with",
      width: "auto",
    });

    // Find the rendered button and click it programmatically
    setTimeout(() => {
      const googleButton = tempContainer.querySelector('div[role="button"]') as HTMLElement;
      if (googleButton) {
        googleButton.click();
      } else {
        // Fallback: try to find any clickable element
        const clickable = tempContainer.querySelector("*") as HTMLElement;
        if (clickable) {
          clickable.click();
        } else {
          document.body.removeChild(tempContainer);
          const error = new Error("Failed to trigger Google Sign-In button.");
          onError?.(error);
        }
      }
    }, 100);
  } catch (error) {
    const err = error instanceof Error ? error : new Error("Failed to trigger Google Sign-In");
    onError?.(err);
    throw err;
  }
};

/**
 * Use OAuth2 popup flow as fallback when FedCM is disabled
 * @param clientId - Google OAuth client ID
 * @param callback - Callback function that receives the idToken
 */
export const signInWithGooglePopup = (
  _clientId: string,
  _callback: (idToken: string) => void
): void => {
  if (!window.google?.accounts?.oauth2) {
    throw new Error("Google Identity Services not loaded. Call loadGoogleScript() first.");
  }

  // For OAuth2 flow, we need to get access token first, then exchange for ID token
  // However, the simpler approach is to use the ID token flow directly
  // Let's use a different approach - render a button instead
  throw new Error("Popup flow requires different implementation. Use renderButton instead.");
};

/**
 * Render Google Sign-In button
 * @param element - HTML element to render the button in
 * @param callback - Callback function that receives the idToken
 */
export const renderGoogleButton = (
  element: HTMLElement,
  callback: (idToken: string) => void,
  clientId: string
): void => {
  if (!window.google?.accounts?.id) {
    throw new Error("Google Identity Services not loaded. Call loadGoogleScript() first.");
  }

  // Initialize with callback
  window.google.accounts.id.initialize({
    client_id: clientId,
    callback: (response) => {
      callback(response.credential);
    },
  });

  // Render button
  window.google.accounts.id.renderButton(element, {
    type: "standard",
    theme: "outline",
    size: "large",
    text: "signin_with",
    width: "100%",
  });
};
